import { Box } from '@moonpig/launchpad-components'
import React, { FC } from 'react'
import { useLoggedIn } from '@moonpig/web-core-auth'
import { useStoreId } from '@moonpig/web-core-stores'
import { ModulePlaceholderVariant } from '@moonpig/web-explore-types-graphql'
import { ModuleErrorBoundary } from '@moonpig/web-shared-products'
import { ContentModule } from '../../types'
import { createContentPipeline } from './pipeline'
import {
  MODULE_CTA_STRIP,
  MODULE_DELIVERY_STRIP,
  MODULE_HEADING,
  MODULE_HERO,
  MODULE_MISSION_BUTTONS,
  MODULE_PLACEHOLDER,
  MODULE_SEARCH_RESULTS,
  MODULE_SEO,
  MODULE_TABBED_PRODUCT_LISTS,
  MODULE_USP_CAROUSEL,
} from '../../modules/constants'
import { getPipelineSteps } from './getPipelineSteps'
import { ModuleTracking } from '../../modules/types'
import { createTracking } from '../../analytics/tracking'
import type { RemindersQueryResult } from '../../pages/ContentPage/remindersQuery'
import { renderPageModule } from '../../modules/renderPageModule'

const LIST_OF_MODULES_WITH_PRODUCT_LIST = [
  MODULE_SEARCH_RESULTS,
  MODULE_TABBED_PRODUCT_LISTS,
]

const LIST_OF_PLACEHOLDER_MODULES_WITH_PRODUCT_CAROUSEL = [
  ModulePlaceholderVariant.Bundles,
]

const hasProductList = (module: ContentModule) => {
  return (
    LIST_OF_MODULES_WITH_PRODUCT_LIST.includes(module.type) ||
    (module.type === MODULE_PLACEHOLDER &&
      LIST_OF_PLACEHOLDER_MODULES_WITH_PRODUCT_CAROUSEL.includes(
        module.placeholderVariant,
      ))
  )
}

const setProductListsTrackingData = (modules: ContentModule[]) => {
  let productListIndexOnPage = 0
  const numberOfProductListsOnPage = modules.filter(hasProductList).length

  return modules.map(module => {
    if (hasProductList(module)) {
      productListIndexOnPage += 1

      return {
        ...module,
        tracking: {
          pageLocation: 'home',
          moduleProductListIndex: productListIndexOnPage,
          moduleProductListsLength: numberOfProductListsOnPage,
        },
      }
    }

    return module
  })
}

const FIXED_BACKGROUND_MODULES = [
  MODULE_CTA_STRIP,
  MODULE_DELIVERY_STRIP,
  MODULE_HERO,
  MODULE_MISSION_BUTTONS,
  MODULE_USP_CAROUSEL,
]

const renderModules = ({
  modules,
  alternatingBackground,
  remindersResult,
}: {
  modules: ContentModule[]
  alternatingBackground: boolean
  remindersResult?: RemindersQueryResult
}) => {
  let trackingDataProps = {
    numberOfProductCarouselsOnPage: 0,
    productCarouselIndexOnPage: 0,
  }
  let nextIndex = 0
  let headingDarkBackground: boolean | null = null

  const modulesWithProductListsTracking = setProductListsTrackingData(modules)

  return modulesWithProductListsTracking.map((module, index) => {
    let darkBackground = false

    if (!FIXED_BACKGROUND_MODULES.includes(module.type)) {
      // eslint-disable-next-line no-plusplus
      darkBackground = nextIndex++ % 2 === 0
    }

    if (module.type === MODULE_HEADING) {
      headingDarkBackground = darkBackground
    }

    if (module.type === MODULE_SEO && headingDarkBackground !== null) {
      darkBackground = headingDarkBackground
    }

    if (hasProductList(module)) {
      const { tracking } = module as ModuleTracking

      trackingDataProps = {
        productCarouselIndexOnPage: tracking.moduleProductListIndex,
        numberOfProductCarouselsOnPage: tracking.moduleProductListsLength,
      }
    }

    const page = 'home'
    const backgroundColor =
      alternatingBackground && darkBackground
        ? 'colorBackground06'
        : 'colorBackground01'
    const context = {
      index,
      backgroundColor,
      padding: { py: { xs: 6, lg: 8 } },
      layout: page,
      tracking: createTracking({ page }),
      ...(remindersResult !== undefined && {
        remindersResult,
      }),
    }

    return (
      // eslint-disable-next-line react/no-array-index-key
      <ModuleErrorBoundary key={index} type={module.type}>
        {renderPageModule({
          module,
          context,
          backgroundColor,
          page,
          trackingData: {
            moduleIndex: index,
            moduleType: module.type,
            ...trackingDataProps,
          },
          ctaStripVariant: 'homepage',
          deliveryStripVariant: 'homepage',
          productListOptions: {
            alternativeButton: true,
            excludeRudeCards: true,
          },
        })}
      </ModuleErrorBoundary>
    )
  })
}

type Props = {
  modules: ContentModule[]
  remindersResult?: RemindersQueryResult
}

const Home: FC<Props> = ({ modules, remindersResult }) => {
  const { loggedIn } = useLoggedIn()
  const storeId = useStoreId()

  const process = createContentPipeline(
    { store: storeId, loggedIn },
    getPipelineSteps({
      loggedIn,
      region: storeId,
    }),
  )

  return (
    <Box bgcolor="white">
      {renderModules({
        modules: process(modules).filter(Boolean),
        alternatingBackground: true,
        remindersResult,
      })}
    </Box>
  )
}

export { Home }
